import {
  CreateReviewResponse,
  ReviewService,
  ReviewView,
} from "../ReviewService";
import { useLanguage } from "../Language";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { FormTextField } from "@tlc/forms/FormTextField";
import { useToast } from "@tlc/ui/toast";
import { RatingHighContrast } from "./RatingHighContrast"; // TODO move to ui package
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { useAnalytics } from "../../analytics/useGoogleAnalytics";

interface ReviewFormInput
  extends Pick<ReviewView, "title" | "description" | "rating" | "name"> {}

interface ReviewDialogProps {
  resource: ResourceDWR | ResourceView;
  defaultRating: number;
  open: boolean;
  onClose: (result: CreateReviewResponse | null) => void;
}

export const ReviewDialog = ({
  resource,
  open,
  defaultRating,
  onClose,
}: ReviewDialogProps) => {
  const { translate } = useLanguage();
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  const { trackEvent } = useAnalytics();
  const formMethods = useForm<ReviewFormInput>({
    mode: "onBlur",
    defaultValues: {
      rating: defaultRating,
      description: "",
      title: "",
      name: "",
    },
  });
  const { reset, control, handleSubmit } = formMethods;

  const onSubmit: SubmitHandler<ReviewFormInput> = async ({
    name,
    description,
    title,
    rating,
  }) => {
    try {
      const response = await ReviewService.createReview({
        name,
        title,
        description,
        resourceId: resource.id,
        rating,
      });
      const { success, approved, blocked } = response;

      if (success) {
        trackEvent("review_add");
        onClose(response);
        if (approved && !blocked) {
          showToast(translate("reviewSavedApproved"), "success");
        } else {
          showToast(translate("reviewSavedAwaitingApproval"), "success");
        }
        reset();
      } else {
        showToast(translate("errorSavingReview"), "error");
      }
    } catch (e: unknown) {
      showToast(translate("errorSavingReview"), "error");
    }
  };
  const handleClose = () => {
    reset();
    onClose(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile}>
      <FormProvider {...formMethods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{translate("addNewReview")}</DialogTitle>
          <DialogContent sx={{ width: isMobile ? "auto" : "600px" }}>
            <Stack spacing={1}>
              <Controller
                name="rating"
                control={control}
                rules={{ required: translate("starRatingIsRequired") }}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => {
                  return (
                    <Box>
                      <Typography
                        component="legend"
                        sx={{
                          color: !!error ? "error.main" : "text.primary",
                        }}
                      >
                        {translate("rateThisTitle")}
                      </Typography>
                      <RatingHighContrast
                        value={value}
                        // pass the value as a number to onChange
                        onChange={(_, value) => onChange(value)}
                        onBlur={onBlur}
                        precision={0.5}
                      />
                      <FormHelperText error={!!error}>
                        {error?.message || " "}
                      </FormHelperText>
                    </Box>
                  );
                }}
              ></Controller>

              <FormTextField<ReviewFormInput>
                name="title"
                label={translate("titleOfReview")}
                maxLength={255}
                required
              />
              <FormTextField<ReviewFormInput>
                name="name"
                maxLength={255}
                label={translate("yourName")}
              />
              <FormTextField<ReviewFormInput>
                name="description"
                maxLength={10000}
                label={translate("writeYourReview")}
                multiline
                rows={7}
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{translate("cancel")}</Button>
            <Button type="submit">{translate("submit")}</Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
